import { l as DocContext, o as Context, k as jsxRuntimeExports, t as textRendererStyle, n as DynamicMath } from "./doenetml-drNYjJiF.js";
import React__default, { useRef, useContext } from "react";
import { j as jxg, c as computeLabelMaskCssStyle } from "./JSXGraphRenderer-BcR36ozd.js";
import { BoardContext, TEXT_LAYER_OFFSET } from "./graph-uNmxqd5I.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-CDZbHBy-.js";
import { g as getPositionFromAnchorByCoordinate } from "./graph-W6vrFcBu.js";
import { ChoiceInputInlineContext } from "./choiceInput-QYG5RDnq.js";
import { u as usePointerDragState, a as useBoardPointerTracking } from "./useBoardPointerTracking-DV6a8Wvg.js";
import { u as useDraggableRefs, a as useJSXGraphCleanup } from "./useJSXGraphCleanup-CX91oVW8.js";
import { d as detachAnchoredGraphElement, a as attachAnchoredGraphDragHandlers } from "./useAnchoredGraphDragHandler-BEy2dmsI.js";
const label = React__default.memo(function Label(props) {
  let { componentIdx, id, SVs, actions, callAction } = useDoenetRenderer(props);
  Label.ignoreActionsWithoutCore = () => true;
  const labelJXG = useRef(null);
  const anchorPointJXG = useRef(null);
  const anchorRel = useRef(null);
  const board = useContext(BoardContext);
  const choiceInputInlineContext = useContext(ChoiceInputInlineContext);
  const pointerState = usePointerDragState();
  const pointAtDown = useRef(null);
  const calculatedX = useRef(null);
  const calculatedY = useRef(null);
  const previousPositionFromAnchor = useRef(null);
  const { fixed, fixLocation, lastPositionFromCore } = useDraggableRefs(SVs, null);
  useBoardPointerTracking(board, pointerState);
  useJSXGraphCleanup({
    objectRef: labelJXG,
    destroy: () => detachAnchoredGraphElement(labelJXG, board)
  });
  const { darkMode } = useContext(DocContext) || {};
  function createLabelJXG() {
    if (board === null) {
      return null;
    }
    let textColor = darkMode === "dark" ? SVs.selectedStyle.textColorDarkMode : SVs.selectedStyle.textColor;
    let backgroundColor = darkMode === "dark" ? SVs.selectedStyle.backgroundColorDarkMode : SVs.selectedStyle.backgroundColor;
    let { cssStyle, highlightCssStyle } = computeLabelMaskCssStyle({
      layer: SVs.layer,
      backgroundColor,
      masked: SVs.maskLabel
    });
    let jsxLabelAttributes = {
      visible: !SVs.hidden,
      fixed: fixed.current,
      layer: 10 * SVs.layer + TEXT_LAYER_OFFSET,
      cssStyle,
      highlightCssStyle,
      strokeColor: textColor,
      strokeOpacity: 1,
      highlightStrokeColor: textColor,
      // Text elements default to a highlightStrokeOpacity < 1, which
      // combines with the background alpha and makes the highlighted
      // mask look transparent (jsxgraph issue #777). Force it to 1.
      highlightStrokeOpacity: 1,
      // JSXGraph's native hover highlight applies the bordered mask
      // style on hover, gated on `highlight` — enabled only when the
      // label is draggable. Native highlight clears reliably on
      // pointer-out (via the board's dehighlightAll), unlike a custom
      // over/out on this HTML-overlay text element.
      highlight: !fixLocation.current,
      useMathJax: SVs.hasLatex,
      parse: false
    };
    let newAnchorPointJXG;
    try {
      let anchor = Context.fromAst(SVs.anchor);
      let anchorCoords = [
        anchor.get_component(0).evaluate_to_constant() ?? NaN,
        anchor.get_component(1).evaluate_to_constant() ?? NaN
      ];
      if (!Number.isFinite(anchorCoords[0])) {
        anchorCoords[0] = 0;
        jsxLabelAttributes["visible"] = false;
      }
      if (!Number.isFinite(anchorCoords[1])) {
        anchorCoords[1] = 0;
        jsxLabelAttributes["visible"] = false;
      }
      newAnchorPointJXG = board.create("point", anchorCoords, {
        visible: false
      });
    } catch (e) {
      jsxLabelAttributes["visible"] = false;
      newAnchorPointJXG = board.create("point", [0, 0], {
        visible: false
      });
    }
    jsxLabelAttributes.anchor = newAnchorPointJXG;
    let { anchorx, anchory } = getPositionFromAnchorByCoordinate(
      SVs.positionFromAnchor
    );
    jsxLabelAttributes.anchorx = anchorx;
    jsxLabelAttributes.anchory = anchory;
    anchorRel.current = [anchorx, anchory];
    let newLabelJXG = board.create(
      "text",
      [0, 0, SVs.value],
      jsxLabelAttributes
    );
    attachAnchoredGraphDragHandlers({
      board,
      newJXG: newLabelJXG,
      newAnchorPoint: newAnchorPointJXG,
      anchorRel,
      pointerState,
      pointAtDown,
      calculatedX,
      calculatedY,
      fixed,
      fixLocation,
      lastPositionFromCore,
      componentIdx,
      actions,
      callAction,
      actionNames: {
        move: "moveLabel",
        focused: "labelFocused",
        clicked: "labelClicked"
      }
    });
    labelJXG.current = newLabelJXG;
    anchorPointJXG.current = newAnchorPointJXG;
    previousPositionFromAnchor.current = SVs.positionFromAnchor;
    if (SVs.hasLatex) {
      setTimeout(() => {
        if (labelJXG.current) {
          labelJXG.current.needsUpdate = true;
          labelJXG.current.setText(SVs.value);
          labelJXG.current.update();
          board?.updateRenderer();
        }
      }, 1e3);
    }
  }
  if (board) {
    let anchorCoords;
    try {
      let anchor = Context.fromAst(SVs.anchor);
      anchorCoords = [
        anchor.get_component(0).evaluate_to_constant() ?? NaN,
        anchor.get_component(1).evaluate_to_constant() ?? NaN
      ];
    } catch (e) {
      anchorCoords = [NaN, NaN];
    }
    lastPositionFromCore.current = anchorCoords;
    if (labelJXG.current === null) {
      createLabelJXG();
    } else {
      labelJXG.current.relativeCoords.setCoordinates(
        jxg.COORDS_BY_USER,
        [0, 0]
      );
      anchorPointJXG.current?.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        anchorCoords
      );
      labelJXG.current.setText(SVs.value);
      let visible = !SVs.hidden;
      if (Number.isFinite(anchorCoords[0]) && Number.isFinite(anchorCoords[1])) {
        let actuallyChangedVisibility = labelJXG.current.visProp["visible"] !== visible;
        labelJXG.current.visProp["visible"] = visible;
        labelJXG.current.visPropCalc["visible"] = visible;
        if (actuallyChangedVisibility) {
          labelJXG.current.setAttribute({ visible });
        }
      } else {
        labelJXG.current.visProp["visible"] = false;
        labelJXG.current.visPropCalc["visible"] = false;
      }
      let layer = 10 * SVs.layer + TEXT_LAYER_OFFSET;
      let layerChanged = labelJXG.current.visProp.layer !== layer;
      if (layerChanged) {
        labelJXG.current.setAttribute({ layer });
      }
      let textColor = darkMode === "dark" ? SVs.selectedStyle.textColorDarkMode : SVs.selectedStyle.textColor;
      let backgroundColor = darkMode === "dark" ? SVs.selectedStyle.backgroundColorDarkMode : SVs.selectedStyle.backgroundColor;
      let { cssStyle, highlightCssStyle } = computeLabelMaskCssStyle({
        layer: SVs.layer,
        backgroundColor,
        masked: SVs.maskLabel
      });
      if (labelJXG.current.visProp.strokecolor !== textColor) {
        labelJXG.current.visProp.strokecolor = textColor;
        labelJXG.current.visProp.highlightstrokecolor = textColor;
      }
      if (labelJXG.current.visProp.cssstyle !== cssStyle) {
        labelJXG.current.visProp.cssstyle = cssStyle;
        labelJXG.current.visProp.highlightcssstyle = highlightCssStyle;
        labelJXG.current.visProp.highlightstrokeopacity = 1;
      }
      labelJXG.current.visProp.highlight = !fixLocation.current;
      labelJXG.current.visProp.fixed = fixed.current;
      labelJXG.current.isDraggable = !fixLocation.current;
      labelJXG.current.needsUpdate = true;
      if (SVs.positionFromAnchor !== previousPositionFromAnchor.current) {
        let { anchorx, anchory } = getPositionFromAnchorByCoordinate(
          SVs.positionFromAnchor
        );
        labelJXG.current.visProp.anchorx = anchorx;
        labelJXG.current.visProp.anchory = anchory;
        anchorRel.current = [anchorx, anchory];
        previousPositionFromAnchor.current = SVs.positionFromAnchor;
        labelJXG.current.fullUpdate();
      } else {
        labelJXG.current.update();
      }
      if (anchorPointJXG.current) {
        anchorPointJXG.current.needsUpdate = true;
        anchorPointJXG.current.update();
      }
      board.updateRenderer();
    }
    return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id });
  }
  if (SVs.hidden) {
    return null;
  }
  const style = !choiceInputInlineContext.inOption ? textRendererStyle(darkMode ?? "light", SVs.selectedStyle) : void 0;
  let label2 = SVs.value;
  if (SVs.hasLatex) {
    label2 = /* @__PURE__ */ jsxRuntimeExports.jsx(DynamicMath, { latex: SVs.value });
  }
  if (SVs.forTargetRendererId) {
    if (SVs.forTargetIsGroup) {
      return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { style, id, children: label2 });
    }
    return /* @__PURE__ */ jsxRuntimeExports.jsx(
      "label",
      {
        style,
        id,
        htmlFor: `${SVs.forTargetRendererId}_input`,
        children: label2
      }
    );
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { style, id, children: label2 });
});
export {
  label as default
};
